import type { ReactNode } from "react";
import { DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import ResponsiveDialog from "shared/ui/ResponsiveDialog";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "shared/hooks/useTranslation";

export interface FormDialogProps {
    open: boolean;
    /** Called when backdrop click / escape / close clicked. Not called when disabled. */
    onClose: () => void;
    title: string;
    subtitle?: string;
    children: ReactNode;
    /** When true, user cannot dismiss via backdrop/escape/button (caller must clear disabled). */
    disabled?: boolean;
    maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
    fullWidth?: boolean;
    scroll?: "body" | "paper";
}

/**
 * Shell for modal forms: consistent title row, close affordance, and padded content with dividers.
 */
export default function FormDialog({
    open,
    onClose,
    title,
    subtitle,
    children,
    disabled = false,
    maxWidth = "sm",
    fullWidth = true,
    scroll = "paper",
}: FormDialogProps) {
    const { t } = useTranslation();

    return (
        <ResponsiveDialog
            open={open}
            onClose={() => {
                if (!disabled) onClose();
            }}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            scroll={scroll}
        >
            <DialogTitle sx={{ pr: 6 }}>
                {title}
                {subtitle ? (
                    <Typography component="p" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {subtitle}
                    </Typography>
                ) : null}
                <IconButton
                    aria-label={t("common.close")}
                    onClick={() => {
                        if (!disabled) onClose();
                    }}
                    disabled={disabled}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                    size="small"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 3 },
                    "& > .MuiCardContent-root": {
                        p: 0,
                        "&:last-child": { pb: 0 },
                    },
                }}
            >
                {children}
            </DialogContent>
        </ResponsiveDialog>
    );
}
